import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import { AOPMaltaStudentData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPDegreeSelect from "@/src/components/AdmissionsOfficePage/shared/AOPDegreeSelect/AOPDegreeSelect";
import AOPIdSourceRow, { AOP_ID_SOURCE_DEFAULT_VALUES } from "@/src/components/AdmissionsOfficePage/shared/AOPIdSourceRow";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill, type AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAOPIdSourceFields } from "@/src/hooks/useAOPIdSourceFields";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import { STUDENT_LETTER_BASIC_REGISTRY, type StudentLetterBasicCountry } from "../studentLetterBasicRegistry";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import { useSyncWithQRPreview } from "@/src/hooks/useSyncWithQRPreview";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import { joinSelectedCourse, splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";
interface IAOPFormValues {
  customize?: boolean;
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  academicYear: string;
  passportNumber: string;
  yearOfStudy: string;
  yearStudyMode: string;
  course: string;
  selectedCourse: string;
  degree?: string;
  mediumOfInstruction: string;
  duration: string;
  studentBody: string;
  studentBodySecond: string;
  secondPageDate: string;
  studentBodyThird: string;
  file: File | null;
  task_id?: string;
  student_id?: string;
  fieldEmail?: boolean;
  userEmail?: string;
  withQR?: boolean;
}

interface AOPConfigProps {
  country: StudentLetterBasicCountry;
  dataForGeneration: AOPMaltaStudentData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaStudentData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaStudentData>>;
  setCustomize: React.Dispatch<React.SetStateAction<boolean>>;
  setWithQRForPreview: React.Dispatch<React.SetStateAction<boolean>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev, setCustomize, setWithQRForPreview }) => {
  const { admissionCourses, studentCourses, studentSecondCourses, studentThirdCourses } = STUDENT_LETTER_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      customize: false,
      studentBody: "",
      studentBodySecond: "",
      studentBodyThird: "",
      currentDate: formatDateForInput(today),
      withQR: true,
      ...AOP_ID_SOURCE_DEFAULT_VALUES,
    },
  });

  const dispatch = useDispatch();
  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    setValue: (name, value) => setValue(name as keyof IAOPFormValues, value as never),
    studentIdFormField: "student_id",
  });
  const [content, setContent] = useState<string>("");
  const [studentContent, setStudentContent] = useState<string>("");
  const [studentContentSecond, setStudentContentSecond] = useState<string>("");
  const [studentContentThird, setStudentContentThird] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const applyIdSourceLockRef = useRef<(payload: AdmissionTaskPrefillPayload) => void>(() => {});

  const customize = watch("customize");
  const course = watch("course");
  const studentBody = watch("studentBody");
  const studentBodySecond = watch("studentBodySecond");
  const studentBodyThird = watch("studentBodyThird");
  const withQR = watch("withQR");
  const fieldEmail = watch("fieldEmail");
  const userEmail = watch("userEmail");

  useSyncWithQRPreview(withQR, setWithQRForPreview);

  const offeredCourseAuto = useAutocomplete(Object.keys(admissionCourses));
  const studentBodyAuto = useAutocomplete(Object.keys(studentCourses));
  const studentBodySecondAuto = useAutocomplete(Object.keys(studentSecondCourses));
  const studentBodyThirdAuto = useAutocomplete(Object.keys(studentThirdCourses));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "student",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto);
      applyIdSourceLockRef.current(payload);
    },
  });

  const idSource = useAOPIdSourceFields<IAOPFormValues>({
    setValue,
    getValues,
    applyTaskPrefill,
    lastPrefilledTaskIdRef,
    fieldEmail: !!fieldEmail,
    userEmail,
  });
  applyIdSourceLockRef.current = idSource.applyIdSourceLockFromPrefill;

  useEffect(() => {
    setCustomize(Boolean(customize));
  }, [customize, setCustomize]);

  useEffect(() => {
    if (!course) {
      setContent("");
      return;
    }
    const key = course as keyof typeof admissionCourses;
    const courseSettings = admissionCourses[key];
    if (courseSettings) {
      setContent(courseSettings.tinyMCEContent);
      const { degree, selectedCourse } = splitSelectedCourse(courseSettings.selected_course);
      setValue("degree", degree);
      setValue("selectedCourse", selectedCourse);
      setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
      setValue("duration", courseSettings.duration);
      setTitle(courseSettings.title);
    } else {
      setContent("");
    }
  }, [course, setValue, admissionCourses]);

  useEffect(() => {
    if (!studentBody) {
      setStudentContent("");
      return;
    }
    const key = studentBody as keyof typeof studentCourses;
    const studentSettings = studentCourses[key];
    setStudentContent(studentSettings ? studentSettings.tinyMCEContent : "");
  }, [studentBody, studentCourses]);

  useEffect(() => {
    if (!studentBodySecond) {
      setStudentContentSecond("");
      return;
    }
    const key = studentBodySecond as keyof typeof studentSecondCourses;
    const studentSettings = studentSecondCourses[key];
    setStudentContentSecond(studentSettings ? studentSettings.tinyMCEContent : "");
  }, [studentBodySecond, studentSecondCourses]);

  useEffect(() => {
    if (!studentBodyThird) {
      setStudentContentThird("");
      return;
    }
    const key = studentBodyThird as keyof typeof studentThirdCourses;
    const studentSettings = studentThirdCourses[key];
    setStudentContentThird(studentSettings ? studentSettings.tinyMCEContent : "");
  }, [studentBodyThird, studentThirdCourses]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;

      const response = await admissionOfficeRequests.createStudentDoc(dataForGeneration);
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      title: title,
      whose_doc: watch("whose_doc"),
      coursePrefix: watch("coursePrefix"),
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      dateOfBirth: watch("dateOfBirth"),
      nationality: watch("nationality"),
      academicYear: watch("academicYear"),
      passportNumber: watch("passportNumber"),
      yearOfStudy: watch("yearOfStudy"),
      yearStudyMode: watch("yearStudyMode"),
      selectedCourse: watch("selectedCourse"),
      degree: watch("degree"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      duration: watch("duration"),
      secondPageDate: watch("secondPageDate"),
      file: watch("file"),
      customize: watch("customize"),
      withQR: watch("withQR"),
    };
    const joinedSelectedCourse = joinSelectedCourse(watchedFields.degree, watchedFields.selectedCourse);
    setDataForGeneration((prev) => ({
      ...prev,
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      need_qr: !!watchedFields.withQR,
      coursePrefix: watchedFields.coursePrefix || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      academicYear: watchedFields.academicYear || "",
      passportNumber: watchedFields.passportNumber || "",
      yearOfStudy: watchedFields.yearOfStudy || "",
      yearStudyMode: watchedFields.yearStudyMode || "",
      course: joinedSelectedCourse || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      duration: watchedFields.duration || "",
      bodyStudentLetter: splitHtmlByBlocks(studentContent),
      secondBodyStudentLetter: customize ? splitHtmlByBlocks(studentContentSecond) : null,
      secondPageDate: watchedFields.secondPageDate ? parseDate(watchedFields.secondPageDate) : "",
      thirdBodyStudentLetter: customize ? splitHtmlByBlocks(studentContentThird) : null,
      file: watchedFields.file,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      coursePrefix: watchedFields.coursePrefix || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      academicYear: watchedFields.academicYear || "",
      passportNumber: watchedFields.passportNumber || "",
      yearOfStudy: watchedFields.yearOfStudy || "",
      yearStudyMode: watchedFields.yearStudyMode || "",
      course: joinedSelectedCourse || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      duration: watchedFields.duration || "",
      bodyStudentLetter: splitHtmlByBlocksWithFontConvert(studentContent),
      secondBodyStudentLetter: customize ? splitHtmlByBlocksWithFontConvert(studentContentSecond) : null,
      secondPageDate: watchedFields.secondPageDate ? parseDate(watchedFields.secondPageDate) : "",
      thirdBodyStudentLetter: customize ? splitHtmlByBlocksWithFontConvert(studentContentThird) : null,
      file: watchedFields.file,
    }));

    const hasContent = content.trim().length > 0 || studentContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleStudentEditorChange = (newstudentContent: string) => setStudentContent(newstudentContent);
  const handleStudentSecondEditorChange = (newstudentContent: string) => setStudentContentSecond(newstudentContent);
  const handleStudentThirdEditorChange = (newstudentContent: string) => setStudentContentThird(newstudentContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as keyof IAOPFormValues);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">STUDENT LETTER</p>
        <form className="admission-office-page-config-form">
          <AOPIdSourceRow
            control={control}
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            applyTaskPrefill={idSource.applyTaskPrefill}
            lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
            lastPrefilledStudentIdRef={idSource.lastPrefilledStudentIdRef}
            fieldEmail={!!fieldEmail}
            isTaskIdDisabled={idSource.isTaskIdDisabled}
            isStudentIdDisabled={idSource.isStudentIdDisabled}
            isFieldEmailDisabled={idSource.isFieldEmailDisabled}
            onClearTaskId={idSource.handleClearTaskId}
            onClearStudentId={idSource.handleClearStudentId}
            onClearUserEmail={idSource.handleClearUserEmail}
            onCheckUserEmail={() => void idSource.handleCheckUserEmail()}
            onCopy={idSource.handleCopyIdField}
          />
          <div className="form-group-fields gap">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.studentBody}
                  inputProps={{
                  register: register("whose_doc"),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                  onArrowClick: whoseDocAuto.onArrowClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input coursePrefix">
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
            </div>
            <div className="form-group-fields-invoice">
              <Controller
                name="customize"
                control={control}
                render={({ field }) => <CustomSwitch label="Customize" value={field.value} onChange={field.onChange} />}
              />
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
                inputProps={{
                register: register("nationality"),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
                onArrowClick: nationalitiesAuto.onArrowClick,
              }}
              identifier={"nationality"}
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input academic-year">
              <div className="form-group-fields-input-label">
                <label htmlFor="academic-year">Academic Year</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="academic-year" id="academic-year" type="text" {...register("academicYear")} />
            </div>
            <div className="form-group-fields-input passport-number">
              <div className="form-group-fields-input-label">
                <label htmlFor="passport-number">Passport Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="passport-number" id="passport-number" type="text" {...register("passportNumber")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input year-of-study">
              <div className="form-group-fields-input-label">
                <label htmlFor="year-of-study">Year of Study</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="year-of-study" id="year-of-study" type="text" {...register("yearOfStudy")} />
            </div>
            <div className="form-group-fields-input year-study-mode">
              <div className="form-group-fields-input-label">
                <label htmlFor="year-study-mode">Year Study Mode</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="year-study-mode" id="year-study-mode" type="text" {...register("yearStudyMode")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                  inputProps={{
                  register: register("course"),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  onArrowClick: offeredCourseAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="degree">Degree</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <AOPDegreeSelect control={control} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse")} />
              </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction")}
              />
              </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration")} />
              </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input studentBody">
              <AutocompleteInput
                label="Selecting details for an student letter"
                value={studentBodyAuto.query}
                onChange={studentBodyAuto.onChange}
                onSelect={(v) => {
                  studentBodyAuto.onSelect(v);
                  setValue("studentBody", v);
                }}
                isOpen={studentBodyAuto.isOpen}
                options={studentBodyAuto.filtered}
                error={!!errors.studentBody}
                  inputProps={{
                  register: register("studentBody"),
                  close: studentBodyAuto.close,
                  setOpen: studentBodyAuto.onClick,
                  onArrowClick: studentBodyAuto.onArrowClick,
                }}
                identifier={"student"}
              />
            </div>
          </div>
          <label>Student letter body</label>
          <Editor
            value={studentContent}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={handleStudentEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          {customize && (
            <>
              <div className="form-group-fields-input secondPageDate">
                <div className="form-group-fields-input-label">
                  <label htmlFor="secondPageDate">
                    Date for second part student letter
                  </label>
                  <div className="form-group-fields-input-label-line"></div>
                </div>
                <input className="secondPageDate" id="secondPageDate" type="date" {...register("secondPageDate")} />
                <div className="form-group-fields-input-svg">
                  <AODSvgExport.DateInputSvg />
                </div>
              </div>
              <div className="form-group-fields">
                <div className="form-group-fields-input studentBodySecond">
                  <AutocompleteInput
                    label="Selecting second page template"
                    value={studentBodySecondAuto.query}
                    onChange={studentBodySecondAuto.onChange}
                    onSelect={(v) => {
                      studentBodySecondAuto.onSelect(v);
                      setValue("studentBodySecond", v);
                    }}
                    isOpen={studentBodySecondAuto.isOpen}
                    options={studentBodySecondAuto.filtered}
                    error={!!errors.studentBody}
                      inputProps={{
                      register: register("studentBodySecond"),
                      close: studentBodySecondAuto.close,
                      setOpen: studentBodySecondAuto.onClick,
                      onArrowClick: studentBodySecondAuto.onArrowClick,
                    }}
                    identifier={"student-second"}
                  />
                </div>
              </div>
              <label>Student letter body (second page)</label>
              <Editor
                value={studentContentSecond}
                init={{
                  height: TINYMCE_EDITOR_HEIGHT,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: TINYMCE_EDITOR_CONTENT_STYLE,
                }}
                onEditorChange={handleStudentSecondEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
              <div className="form-group-fields">
                <div className="form-group-fields-input studentBodyThird">
                  <AutocompleteInput
                    label="Selecting third page template"
                    value={studentBodyThirdAuto.query}
                    onChange={studentBodyThirdAuto.onChange}
                    onSelect={(v) => {
                      studentBodyThirdAuto.onSelect(v);
                      setValue("studentBodyThird", v);
                    }}
                    isOpen={studentBodyThirdAuto.isOpen}
                    options={studentBodyThirdAuto.filtered}
                    error={!!errors.studentBody}
                      inputProps={{
                      register: register("studentBodyThird"),
                      close: studentBodyThirdAuto.close,
                      setOpen: studentBodyThirdAuto.onClick,
                      onArrowClick: studentBodyThirdAuto.onArrowClick,
                    }}
                    identifier={"student-third"}
                  />
                </div>
              </div>
              <label>Student letter body (third page)</label>
              <Editor
                value={studentContentThird}
                init={{
                  height: TINYMCE_EDITOR_HEIGHT,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: TINYMCE_EDITOR_CONTENT_STYLE,
                }}
                onEditorChange={handleStudentThirdEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          <div className="form-group-fields">
            <div className="form-group-fields-input file">
              <label htmlFor="file">Chose photo:</label>
              <input
                type="file"
                id="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setValue("file", file, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            </div>
          </div>
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields">
            <div className="form-group-fields-invoice">
              <Controller
                name="withQR"
                control={control}
                render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
